import { createElement, type FormHTMLAttributes, type HTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { cx, gapAttr, type GapStep } from './library-kit';
import { Text, type TextProps, type TextTone } from './text';
import type { Align } from './layout';

/**
 * D550 (ADMIN-A, design-fidelity wave, phase B): the inline-level companions of the layout vocabulary. `Stack` and `Row` are
 * block-level (`div`-like); inside a table cell, a paragraph or a line of text a screen needs the same gap/align vocabulary on a
 * `span`. `Ink` is the colour/weight of a run of text that keeps the size of its context (a hint inside a caption, a bold word);
 * `Strong` is `Text` with the emphasis weight where a role's own weight is lighter (13px at 600). Like the rest of the vocabulary
 * none of them accepts `style`, and every option is a token NAME written as a data attribute.
 */

type SpanAttrs = Omit<HTMLAttributes<HTMLElement>, 'style' | 'className' | 'children'>;

export interface InlineRowProps extends SpanAttrs {
  /** Space step between items. Default 3 (12px). */
  readonly gap?: GapStep;
  /** Cross-axis alignment. Default `center`. */
  readonly align?: Align;
  readonly justify?: 'start' | 'center' | 'end' | 'between';
  readonly wrap?: boolean;
  readonly children?: ReactNode;
}

/** `Row` on an inline box (`display: inline-flex`): a run of items inside a line, a cell or a button label. */
export function InlineRow({ gap, align, justify, wrap = false, children, style: _style, ...rest }: InlineRowProps & { style?: unknown }): ReactElement {
  return createElement(
    'span',
    { ...rest, className: 'vello-cluster', 'data-inline': 'true', 'data-gap': gapAttr(gap), 'data-align': align, 'data-justify': justify, 'data-wrap': wrap ? 'true' : undefined },
    children,
  );
}

export interface InlineStackProps extends SpanAttrs {
  readonly gap?: GapStep;
  readonly align?: Align;
  readonly children?: ReactNode;
}

/** `Stack` on an inline box (`display: inline-flex`, column): two lines of text that belong to one cell. */
export function InlineStack({ gap, align, children, style: _style, ...rest }: InlineStackProps & { style?: unknown }): ReactElement {
  return createElement('span', { ...rest, className: 'vello-stack', 'data-inline': 'true', 'data-gap': gapAttr(gap), 'data-align': align }, children);
}

export interface InkProps extends Omit<HTMLAttributes<HTMLElement>, 'style' | 'className' | 'children' | 'role'> {
  /** The ink. Default `primary`. */
  readonly tone?: TextTone;
  /** The emphasis weight (the label weight, 600). */
  readonly strong?: boolean;
  /** Fill the line. */
  readonly block?: boolean;
  /** Text alignment inside the box (a numeric or action cell on the right). */
  readonly align?: 'center' | 'end';
  /** Data: IBM Plex Mono, tabular (an id, a code, an amount). */
  readonly mono?: boolean;
  /** Takes the free width of a wrapping row (a name beside its actions). */
  readonly grow?: boolean;
  /** Keep the line breaks of a message. */
  readonly preLine?: boolean;
  /** One line with an ellipsis. */
  readonly truncate?: boolean;
  /** A long token (an id, an address) may break anywhere. */
  readonly breakAnywhere?: boolean;
  /** The element. Default `span`. */
  readonly as?: 'span' | 'p' | 'div' | 'strong' | 'em' | 'small' | 'li' | 'dd' | 'dt' | 'legend' | 'label' | 'h2' | 'h3' | 'h4' | 'code';
  /** `htmlFor`, when `as="label"`. */
  readonly htmlFor?: string;
  readonly children?: ReactNode;
}

/** A colour and/or weight that keeps the size, leading and family of its context (unlike `Text`, which sets a type role). */
export function Ink({ tone = 'primary', strong = false, block = false, align, mono = false, grow = false, preLine = false, truncate = false, breakAnywhere = false, as = 'span', children, style: _style, ...rest }: InkProps & { style?: unknown }): ReactElement {
  return createElement(
    as,
    { ...rest, className: 'vello-admin-a-ink', 'data-tone': tone === 'primary' ? undefined : tone, 'data-strong': strong ? 'true' : undefined, 'data-block': block ? 'true' : undefined, 'data-align': align, 'data-mono': mono ? 'true' : undefined, 'data-grow': grow ? 'true' : undefined, 'data-pre': preLine ? 'true' : undefined, 'data-truncate': truncate ? 'true' : undefined, 'data-break': breakAnywhere ? 'anywhere' : undefined },
    children,
  );
}

/** `Text` where the emphasis weight is wanted on top of the role (a 13px caption at 600). */
export function Strong({ className, ...props }: TextProps): ReactElement {
  return <Text {...props} className={cx('vello-admin-a-strong', className)} />;
}

export interface FormStackProps extends Omit<FormHTMLAttributes<HTMLFormElement>, 'style' | 'className' | 'children'> {
  /** Space step between the fields. Default 4 (16px). */
  readonly gap?: GapStep;
  /** A hairline above the form and padding under it (a form slot after another block). */
  readonly divided?: boolean;
  readonly align?: Align;
  readonly children?: ReactNode;
}

/** A `<form>` laid out as a `Stack` — keeps the form's own typed attributes (`onSubmit`, `noValidate`, `autoComplete`). */
export function FormStack({ gap, align, divided = false, children, style: _style, ...rest }: FormStackProps & { style?: unknown }): ReactElement {
  return (
    <form {...rest} className={divided ? 'vello-stack vello-admin-a-divided' : 'vello-stack'} data-gap={gapAttr(gap)} data-align={align}>
      {children}
    </form>
  );
}
